// Scheduled maintenance (board system/maintenance). Two sources, one store:
//
//   1. GET /api/business/v1/config — the platform setting. Today the platform
//      sends `settings.maintenance: bool` (ihjizli-platform appconfig.Settings);
//      the spec's richer shape `maintenance: { active, resumes_at, message? }`
//      (top level or under settings) is read too, and wins when present.
//   2. Any response 503 whose problem `code` is "maintenance" (api.ts) — with
//      the return time from the problem's `resumes_at` or the Retry-After header.
//
// The store only says WHETHER a window is on and WHEN it ends (if the server
// said). The app root redirects to /maintenance while it is on; the page polls
// the config and returns to the app when the window ends. Node mode has no
// cloud, so none of this applies there (erp-context skips it).
//
// «العمل بلا اتصال» on the maintenance page = `workOffline()`: the person
// chooses to keep working on the local queue; the redirect stops for this
// window (until the window ends or a new one starts).

import { useSyncExternalStore } from 'react';
import { onlineStore } from './online';

export interface MaintenanceWindow {
  active: boolean;
  /** ISO time the service returns, when the server gave one. */
  resumesAt: string | null;
}

export const MAINTENANCE_CODE = 'maintenance';

const OFF: MaintenanceWindow = { active: false, resumesAt: null };

function isoOrNull(v: unknown): string | null {
  if (typeof v !== 'string' || !v.trim()) return null;
  return Number.isNaN(new Date(v).getTime()) ? null : v;
}

/** The maintenance window a /v1/config payload reports (fails OPEN to "not active"). */
export function maintenanceFromConfig(raw: unknown): MaintenanceWindow {
  if (!raw || typeof raw !== 'object') return OFF;
  const root = raw as Record<string, unknown>;
  const settings = (root.settings && typeof root.settings === 'object' ? root.settings : {}) as Record<string, unknown>;
  const obj = [root.maintenance, settings.maintenance].find((m) => m && typeof m === 'object') as Record<string, unknown> | undefined;
  if (obj) return { active: obj.active === true, resumesAt: obj.active === true ? isoOrNull(obj.resumes_at ?? obj.resumesAt) : null };
  if (settings.maintenance === true) {
    return { active: true, resumesAt: isoOrNull(settings.maintenance_resumes_at ?? settings.maintenanceResumesAt) };
  }
  return OFF;
}

/**
 * Retry-After (seconds or an HTTP date) → ISO, relative to `now`. Null when
 * absent or unparseable.
 */
export function retryAfterToIso(header: string | null | undefined, now: number = Date.now()): string | null {
  if (!header) return null;
  const s = header.trim();
  if (/^\d+$/.test(s)) return new Date(now + Number(s) * 1000).toISOString();
  const t = new Date(s).getTime();
  return Number.isNaN(t) ? null : new Date(t).toISOString();
}

type Listener = () => void;
const listeners = new Set<Listener>();
/**
 * Where a window was reported: the platform config, or a 503 maintenance from
 * one service (`api:erp`, `api:business`, …) — a later normal answer from THAT
 * service lifts its part.
 */
export type MaintenanceSource = 'config' | `api:${string}`;

const sources = new Map<MaintenanceSource, MaintenanceWindow>();
let current: MaintenanceWindow = OFF;
let offlineChosen = false;

function emit() {
  listeners.forEach((l) => l());
}

function combine(): MaintenanceWindow {
  const on = [...sources.entries()].filter(([, w]) => w.active);
  if (on.length === 0) return OFF;
  // The platform's return time first, else any service's.
  on.sort(([a], [b]) => (a === 'config' ? -1 : b === 'config' ? 1 : 0));
  return { active: true, resumesAt: on.map(([, w]) => w.resumesAt).find((r) => r != null) ?? null };
}

export const maintenanceStore = {
  get: (): MaintenanceWindow => current,
  /** True while a window is on and the person did not choose to work offline. */
  blocks: (): boolean => current.active && !offlineChosen,
  workingOffline: (): boolean => current.active && offlineChosen,
  /** A 503 maintenance from a service (any, or the named one) holds the window. */
  heldByApi: (service?: string): boolean =>
    service ? sources.get(`api:${service}`)?.active === true : [...sources.entries()].some(([k, w]) => k !== 'config' && w.active),
  subscribe(l: Listener): () => void {
    listeners.add(l);
    return () => listeners.delete(l);
  },
  /**
   * Report what a source said. The window is on while ANY source says so; when
   * it ends, the offline mode chosen for it ends too (back online, the local
   * queue replays).
   */
  report(w: MaintenanceWindow, source: MaintenanceSource = 'config'): void {
    const prev = sources.get(source) ?? OFF;
    if (w.active) sources.set(source, { active: true, resumesAt: w.resumesAt ?? (prev.active ? prev.resumesAt : null) });
    else sources.delete(source);
    const next = combine();
    const wasOffline = offlineChosen;
    if (!next.active && offlineChosen) offlineChosen = false;
    const changed = next.active !== current.active || next.resumesAt !== current.resumesAt;
    current = next;
    if (wasOffline && !offlineChosen) onlineStore.set(true);
    if (changed || wasOffline !== offlineChosen) emit();
  },
  /** «العمل بلا اتصال»: stop redirecting for this window. */
  workOffline(): void {
    if (offlineChosen) return;
    offlineChosen = true;
    emit();
  },
  /** Test hook. */
  reset(): void {
    sources.clear();
    current = OFF;
    offlineChosen = false;
    emit();
  },
};

function snapshot(): string {
  return `${current.active ? 1 : 0}|${current.resumesAt ?? ''}|${offlineChosen ? 1 : 0}`;
}

/** { window, blocks } — re-renders on every change. */
export function useMaintenance(): { window: MaintenanceWindow; blocks: boolean; workingOffline: boolean } {
  useSyncExternalStore(maintenanceStore.subscribe, snapshot, snapshot);
  return { window: maintenanceStore.get(), blocks: maintenanceStore.blocks(), workingOffline: maintenanceStore.workingOffline() };
}

/** /maintenance?until=<ISO> */
export function maintenancePath(w: MaintenanceWindow = current): string {
  return w.resumesAt ? `/maintenance?until=${encodeURIComponent(w.resumesAt)}` : '/maintenance';
}
